/**
 * @file Contains helpers for the Permissions settings.
 * */
import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { __ } from '@@/bits/intl'
import {
  AllowedDefaultWallRole,
  LibraryType,
  WallAccessDefault,
  WallAccessRole,
  WallAccessRule,
  WallPermission,
  WallPrivacy,
  WallPrivacyOption,
} from '@@/enums'
import {
  ConfirmationDialogLayoutVariant,
  OzConfirmationDialogBoxButtonScheme,
} from '@@/pinia/global_confirmation_dialog'

const DEFAULT_WALL_PRIVACY_TEXT = {
  title: __('Default privacy'),
  description: __('Select the privacy that is set when new padlets are made.'),
}

const DEFAULT_WALL_PERMISSION_TEXT = {
  title: __('Default visitor permission'),
  description: __('Select the visitor permission that is set when new padlets are made.'),
}

const generateWallVisitorAccessText = (
  libraryName: string,
  type: 'student' | 'teacher' | 'member',
): {
  title: string
  description: string
} => {
  return {
    title: {
      student: __('Visitors outside %{libraryName} can access student padlets', {
        libraryName,
      }),
      teacher: __('Visitors outside %{libraryName} can access teacher padlets', {
        libraryName,
      }),
      member: __('Visitors outside %{libraryName} can access member padlets', {
        libraryName,
      }),
    }[type],
    description: {
      student: __('Disabling this will force visitor permissions to "No access" on student padlets.'),
      teacher: __('Disabling this will force visitor permissions to "No access" on teacher padlets.'),
      member: __('Disabling this will force visitor permissions to "No access" on member padlets.'),
    }[type],
  }
}

const generateWallMemberAccessText = (
  libraryName: string,
  type: 'student' | 'teacher' | 'member',
): {
  title: string
  description: string
} => {
  return {
    title: {
      student: __('Members of %{libraryName} can access student padlets', {
        libraryName,
      }),
      teacher: __('Members of %{libraryName} can access teacher padlets', {
        libraryName,
      }),
      member: __('Members of %{libraryName} can access member padlets', {
        libraryName,
      }),
    }[type],
    description: {
      student: __('Disabling this will force member permissions to "No access" on student padlets.'),
      teacher: __('Disabling this will force member permissions to "No access" on teacher padlets.'),
      member: __('Disabling this will force member permissions to "No access" on member padlets.'),
    }[type],
  }
}

const generateWallVisitorPermissionText = (
  libraryName: string,
  type: 'student' | 'teacher' | 'member',
): {
  title: string
  description: string
} => {
  return {
    title: __('Default permissions for visitors outside %{libraryName}', {
      libraryName,
    }),
    description: {
      student: __('Select the visitor permissions that are set when new padlets are made by students.'),
      teacher: __('Select the visitor permissions that are set when new padlets are made by teachers.'),
      member: __('Select the visitor permissions that are set when new padlets are made by members.'),
    }[type],
  }
}

const generateWallMemberPermissionText = (
  libraryName: string,
  type: 'student' | 'teacher' | 'member',
): {
  title: string
  description: string
} => {
  return {
    title: __('Default permissions for %{libraryName} members', {
      libraryName,
    }),
    description: {
      student: __(
        'Select the permissions for %{libraryName} members that are set when new padlets are made by students.',
        { libraryName },
      ),
      teacher: __(
        'Select the permissions for %{libraryName} members that are set when new padlets are made by teachers.',
        {
          libraryName,
        },
      ),
      member: __(
        'Select the permissions for %{libraryName} members that are set when new padlets are made by members.',
        { libraryName },
      ),
    }[type],
  }
}

enum PrivacyOptionsPermissionV2 {
  RequirePassword = 'require_password',
  RequireLogin = 'require_login',
  ListedSetting = 'listed_setting',
  PublishableSetting = 'publishable_setting',
}

const generatePrivacyOptionsPermissionV2Text = (
  privacyOption: PrivacyOptionsPermissionV2,
  libraryName: string,
  type: 'student' | 'teacher' | 'member',
): {
  title: string
  description: string
} => {
  return {
    [PrivacyOptionsPermissionV2.RequirePassword]: {
      title: __('Require password'),
      description: {
        student: __('Students can require visitors to enter a password to access their padlets.'),
        teacher: __('Teachers can require visitors to enter a password to access their padlets.'),
        member: __('Members can require visitors to enter a password to access their padlets.'),
      }[type],
    },
    [PrivacyOptionsPermissionV2.RequireLogin]: {
      title: __('Require log in'),
      description: {
        student: __('Students can require visitors to log in to access their padlets.'),
        teacher: __('Teachers can require visitors to log in to access their padlets.'),
        member: __('Members can require visitors to log in to access their padlets.'),
      }[type],
    },
    [PrivacyOptionsPermissionV2.ListedSetting]: {
      title: __('Show padlets in %{libraryName} dashboard', {
        libraryName,
      }),
      description: {
        student: __('Students can display their padlets in %{libraryName} dashboard.', {
          libraryName,
        }),
        teacher: __('Teachers can display their padlets in %{libraryName} dashboard.', {
          libraryName,
        }),
        member: __('Members can display their padlets in %{libraryName} dashboard.', {
          libraryName,
        }),
      }[type],
    },
    [PrivacyOptionsPermissionV2.PublishableSetting]: {
      title: __('Publish padlets to profile and web'),
      description: {
        student: __('Students can publish their padlets to be viewed on their profiles and through Google search.'),
        teacher: __('Teachers can publish their padlets to be viewed on their profiles and through Google search.'),
        member: __('Members can publish their padlets to be viewed on their profiles and through Google search.'),
      }[type],
    },
  }[privacyOption]
}

const generateTenantWallPrivacyText = (
  privacy: WallPrivacy,
  tenantName?: string,
): {
  title: string
  description: string
} => {
  const name = tenantName ?? __('your organization')
  return {
    [WallPrivacy.Private]: {
      title: __('Private'),
      description: __(
        'Allows setting visitor permissions to no access so only the padlet creator and collaborators can access it.',
      ),
    },
    [WallPrivacy.OrgWideUnlisted]: {
      title: __('Org wide - Hidden'),
      description: __('Only members of %{tenantName} can access these padlets. They are hidden from the dashboard.', {
        tenantName: name,
      }),
    },
    [WallPrivacy.OrgWideListed]: {
      title: __('Org wide - Dashboard'),
      description: __('Only members of %{tenantName} can access these padlets. They appear on the dashboard.', {
        tenantName: name,
      }),
    },
    [WallPrivacy.PasswordProtected]: {
      title: __('Secret - Password'),
      description: __(
        'Anyone with the link and password can access these padlets. A random password is generated when a padlet is created.',
      ),
    },
    [WallPrivacy.Secret]: {
      title: __('Secret'),
      description: __('Any visitor with the link can access these padlets.'),
    },
  }[privacy]
}

const generateWallPermissionText = (
  permission: WallPermission,
): {
  title: string
  description: string
} => {
  return {
    [WallPermission.Read]: {
      title: __('Reader'),
      description: __('Visitors can access this padlet and read posts.'),
    },
    [WallPermission.Write]: {
      title: __('Writer'),
      description: __('Visitors can write new posts.'),
    },
  }[permission]
}

const generateAccessOptionsText = (
  access: WallAccessRule,
  type: 'student' | 'teacher' | 'member',
): {
  title: string
  description: string
} => {
  return {
    [WallAccessRule.NotAllowed]: {
      title: __('Not allowed'),
      description: {
        student: __('Padlets are set to "No access" and students can\'t modify this setting.'),
        teacher: __('Padlets are set to "No access" and teachers can\'t modify this setting.'),
        member: __('Padlets are set to "No access" and members can\'t modify this setting.'),
      }[type],
    },
    [WallAccessRule.Allowed]: {
      title: __('Allowed'),
      description: {
        student: __('Students can modify access on their padlets.'),
        teacher: __('Teachers can modify access on their padlets.'),
        member: __('Members can modify access on their padlets.'),
      }[type],
    },
    [WallAccessRule.Required]: {
      title: __('Required'),
      description: {
        student: __("Padlets are always accessible and students can't remove access."),
        teacher: __("Padlets are always accessible and teachers can't remove access."),
        member: __("Padlets are always accessible and members can't remove access."),
      }[type],
    },
  }[access]
}

const generateDefaultAccessOptionsText = (
  defaultAccess: WallAccessDefault,
  type: 'student' | 'teacher' | 'member',
): {
  title: string
  description: string
} => {
  return {
    [WallAccessDefault.NotAllowed]: {
      title: __('Not allowed'),
      description: {
        student: __('This option is toggled off for all student padlets and cannot be modified.'),
        teacher: __('This option is toggled off for all teacher padlets and cannot be modified.'),
        member: __('This option is toggled off for all member padlets and cannot be modified.'),
      }[type],
    },
    [WallAccessDefault.DefaultOff]: {
      title: __('Allowed - Default off'),
      description: {
        student: __('This option defaults to off for new padlets but students can modify it.'),
        teacher: __('This option defaults to off for new padlets but teachers can modify it.'),
        member: __('This option defaults to off for new padlets but members can modify it.'),
      }[type],
    },
    [WallAccessDefault.DefaultOn]: {
      title: __('Allowed - Default on'),
      description: {
        student: __('This option defaults to on for new padlets but students can modify it.'),
        teacher: __('This option defaults to on for new padlets but teachers can modify it.'),
        member: __('This option defaults to on for new padlets but members can modify it.'),
      }[type],
    },
    [WallAccessDefault.Required]: {
      title: __('Required'),
      description: {
        student: __('This option is toggled on for all student padlets and cannot be modified.'),
        teacher: __('This option is toggled on for all teacher padlets and cannot be modified.'),
        member: __('This option is toggled on for all member padlets and cannot be modified.'),
      }[type],
    },
  }[defaultAccess]
}

const generateWallRoleTextLowercase = (role: AllowedDefaultWallRole | WallAccessRole): string => {
  return {
    [WallAccessRole.None]: __('no access'),
    [WallAccessRole.Reader]: __('reader'),
    [WallAccessRole.Commenter]: __('commenter'),
    [WallAccessRole.Writer]: __('writer'),
    [WallAccessRole.Moderator]: __('moderator'),
    [WallAccessRole.Admin]: __('admin'),
  }[role]
}

const generateWallRoleText = (
  role: AllowedDefaultWallRole,
  type: 'visitor' | 'member',
  organizationName?: string,
): {
  title: string
  description: string
} => {
  return {
    [AllowedDefaultWallRole.None]: {
      title: __('No access'),
      description: {
        visitor: __('Visitors cannot access this padlet.'),
        member: organizationName
          ? __('%{organizationName} members cannot access this padlet.', { organizationName })
          : __('Members cannot access this padlet.'),
      }[type],
    },
    [AllowedDefaultWallRole.Reader]: {
      title: __('Reader'),
      description: {
        visitor: __('Visitors can access this padlet and read posts.'),
        member: organizationName
          ? __('%{organizationName} members can access this padlet and read posts.', { organizationName })
          : __('Members can access this padlet and read posts.'),
      }[type],
    },
    [AllowedDefaultWallRole.Commenter]: {
      title: __('Commenter'),
      description: {
        visitor: __('Visitors can comment on posts and add reactions.'),
        member: organizationName
          ? __('%{organizationName} members can comment on posts and add reactions.', { organizationName })
          : __('Members can comment on posts and add reactions.'),
      }[type],
    },
    [AllowedDefaultWallRole.Writer]: {
      title: __('Writer'),
      description: {
        visitor: __('Visitors can write new posts.'),
        member: organizationName
          ? __('%{organizationName} members can write new posts.', { organizationName })
          : __('Members can write new posts.'),
      }[type],
    },
  }[role]
}

const generateEducationLibraryWallPrivacyText = (
  privacy: WallPrivacyOption,
  libraryName?: string,
  libraryType?: LibraryType,
): {
  title: string
  description: string
} => {
  const name = libraryName ?? __('your organization')
  return {
    [WallPrivacyOption.Private]: {
      title: __('Private'),
      description: __(
        'Allows setting visitor permissions to no access so only the padlet creator and collaborators can access it.',
      ),
    },
    [WallPrivacyOption.LibraryWideUnlisted]: {
      title: libraryType === LibraryType.School ? __('School wide - Hidden') : __('Classroom wide - Hidden'),
      description: __('Only members of %{libraryName} can access these padlets. They are hidden from the dashboard.', {
        libraryName: name,
      }),
    },
    [WallPrivacyOption.LibraryWideListed]: {
      title: libraryType === LibraryType.School ? __('School wide - Dashboard') : __('Classroom wide - Dashboard'),
      description: __('Only members of %{libraryName} can access these padlets. They appear on the dashboard.', {
        libraryName: name,
      }),
    },
    [WallPrivacyOption.PasswordProtected]: {
      title: __('Secret - Password'),
      description: __(
        'Anyone with the link and password can access these padlets. A random password is generated when a padlet is created.',
      ),
    },
    [WallPrivacyOption.LoggedInUsersOnly]: {
      title: __('Secret - Log in'),
      description: __('Any logged in visitor with the link can access these padlets.'),
    },
    [WallPrivacyOption.Secret]: {
      title: __('Secret - All visitors'),
      description: __('Anyone with the link can access these padlets.'),
    },
    [WallPrivacyOption.Public]: {
      title: __('Public'),
      description: __("Anyone can access these padlets. They appear on the member's profile and in Google searches."),
    },
  }[privacy]
}

const defaultVisitorRoles = <T extends string>(
  key: T,
  currentRole: AllowedDefaultWallRole,
  type: 'visitor' | 'member',
  includeNoAccess: boolean = true,
  onRoleSelect: (key: T, role: AllowedDefaultWallRole) => void,
  organizationName?: string,
): Array<{
  title: string
  description: string
  value: AllowedDefaultWallRole
  isSelected: boolean
  select: () => void
}> => {
  const roles = [AllowedDefaultWallRole.Reader, AllowedDefaultWallRole.Commenter, AllowedDefaultWallRole.Writer]

  if (includeNoAccess || currentRole === AllowedDefaultWallRole.None) {
    roles.unshift(AllowedDefaultWallRole.None)
  }

  return roles.map((role) => ({
    ...generateWallRoleText(role, type, organizationName),
    value: role,
    isSelected: currentRole === role,
    select: () => {
      if (currentRole === role) return
      onRoleSelect(key, role)
    },
  }))
}

/**
 * Check if a permission change will trigger retroactive wall updates for V2 access control settings
 */
const willTriggerRetroactiveWallUpdatesV2 = (
  key: string,
  setting: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
  accessControlV2Permissions: string[],
): boolean => {
  // Only WallAccessRule settings can trigger retroactive updates
  if (!(setting === WallAccessRule.Required || setting === WallAccessRule.NotAllowed)) {
    return false
  }

  return accessControlV2Permissions.includes(key)
}

/**
 * Generate the retroactive wall updates confirmation dialog options
 */
const getRetroactiveWallUpdatesConfirmationOptions = (
  userType: 'student' | 'teacher' | 'member',
  permissionKey?: string,
  settingValue?: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole,
): {
  title: string
  body: string
  confirmButtonText: string
  cancelButtonText: string
} => {
  // Handle password-specific messaging
  if (permissionKey === 'requirePassword') {
    const isAddingPasswords = settingValue === WallAccessRule.Required
    const passwordBodyMessages = {
      student: isAddingPasswords
        ? __('This action will add passwords to all existing student padlets in your school. This cannot be undone.')
        : __(
            'This action will remove passwords from all existing student padlets in your school. This cannot be undone.',
          ),
      teacher: isAddingPasswords
        ? __('This action will add passwords to all existing teacher padlets in your school. This cannot be undone.')
        : __(
            'This action will remove passwords from all existing teacher padlets in your school. This cannot be undone.',
          ),
      member: isAddingPasswords
        ? __('This action will add passwords to all existing member padlets in your team. This cannot be undone.')
        : __('This action will remove passwords from all existing member padlets in your team. This cannot be undone.'),
    }

    return {
      title: isAddingPasswords ? __('Passwords will be added') : __('Passwords will be removed'),
      body: passwordBodyMessages[userType],
      confirmButtonText: isAddingPasswords ? __('Add passwords') : __('Remove passwords'),
      cancelButtonText: __('Nevermind'),
    }
  }

  // Handle publishable setting specific messaging
  if (permissionKey === 'publishableSetting' && settingValue === WallAccessRule.Required) {
    const publishableBodyMessages = {
      student: __(
        'This action will expose all existing student padlets to the public by publishing them to student profiles and Google search. Please be very careful in doing this.',
      ),
      teacher: __(
        'This action will expose all existing teacher padlets to the public by publishing them to teacher profiles and Google search. Please be very careful in doing this.',
      ),
      member: __(
        'This action will expose all existing member padlets to the public by publishing them to member profiles and Google search. Please be very careful in doing this.',
      ),
    }

    const publishableTitleMessages = {
      student: __('Student padlets will be public'),
      teacher: __('Teacher padlets will be public'),
      member: __('Member padlets will be public'),
    }

    const publishableConfirmMessages = {
      student: __('Publish all student padlets'),
      teacher: __('Publish all teacher padlets'),
      member: __('Publish all member padlets'),
    }

    return {
      title: publishableTitleMessages[userType],
      body: publishableBodyMessages[userType],
      confirmButtonText: publishableConfirmMessages[userType],
      cancelButtonText: __('Nevermind'),
    }
  }

  // Default messaging for other permissions
  const bodyMessages = {
    student: __('Changing this permission will update privacy and access for existing student padlets in your school.'),
    teacher: __('Changing this permission will update privacy and access for existing teacher padlets in your school.'),
    member: __('Changing this permission will update privacy and access for existing member padlets in your team.'),
  }

  return {
    title: __('Update permissions?'),
    body: bodyMessages[userType],
    confirmButtonText: __('Confirm'),
    cancelButtonText: __('Nevermind'),
  }
}

/**
 * Create the retroactive wall updates confirmation dialog configuration
 */
const createRetroactiveWallUpdatesConfirmationDialog = ({
  userType,
  isPhone,
  afterConfirmAction,
  permissionKey,
  settingValue,
}: {
  userType: 'student' | 'teacher' | 'member'
  isPhone: boolean
  afterConfirmAction: () => void
  permissionKey?: string
  settingValue?: WallAccessRule | WallAccessDefault | AllowedDefaultWallRole
}) => {
  const dialogOptions = getRetroactiveWallUpdatesConfirmationOptions(userType, permissionKey, settingValue)

  // Use alert icon for all retroactive updates since they modify existing padlets
  return {
    ...ALERT_ICON,
    ...dialogOptions,
    layoutVariant: isPhone ? ConfirmationDialogLayoutVariant.Drawer : ConfirmationDialogLayoutVariant.Small,
    buttonScheme: OzConfirmationDialogBoxButtonScheme.Default,
    afterConfirmCallbacks: [afterConfirmAction],
  }
}

export {
  createRetroactiveWallUpdatesConfirmationDialog,
  DEFAULT_WALL_PERMISSION_TEXT,
  DEFAULT_WALL_PRIVACY_TEXT,
  defaultVisitorRoles,
  generateAccessOptionsText,
  generateDefaultAccessOptionsText,
  generateEducationLibraryWallPrivacyText,
  generatePrivacyOptionsPermissionV2Text,
  generateTenantWallPrivacyText,
  generateWallMemberAccessText,
  generateWallMemberPermissionText,
  generateWallPermissionText,
  generateWallRoleText,
  generateWallRoleTextLowercase,
  generateWallVisitorAccessText,
  generateWallVisitorPermissionText,
  getRetroactiveWallUpdatesConfirmationOptions,
  PrivacyOptionsPermissionV2,
  willTriggerRetroactiveWallUpdatesV2,
}
